/* Offline photo queue.

   Facility photos captured while offline (or when an immediate upload fails) are stashed in
   IndexedDB and uploaded on reconnect. Blobs are used so they survive a reload or app close —
   which the PWA offline shell now makes a real scenario in the field.

   Device-local by design: only the device that took the photo holds the blob, so a pending photo
   is NOT written into the shared assessment document until it actually uploads. That keeps other
   devices/users from seeing a broken entry they have no blob for. */

export interface PendingPhoto {
  localId: string;
  orgId: string | null;
  projectId: string;
  assessmentId: string;
  assetId: string;
  blob: Blob;
  createdAt: string;
}

const DB_NAME = 'mep-tools-offline';
const STORE = 'pendingPhotos';
const VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'localId' });
        store.createIndex('byAssessment', 'assessmentId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueuePhoto(rec: PendingPhoto): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite');
    t.objectStore(STORE).put(rec);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  db.close();
}

export async function getPendingForAssessment(assessmentId: string): Promise<PendingPhoto[]> {
  const db = await openDB();
  const recs = await new Promise<PendingPhoto[]>((resolve, reject) => {
    const t = db.transaction(STORE, 'readonly');
    const req = t.objectStore(STORE).index('byAssessment').getAll(assessmentId);
    req.onsuccess = () => resolve(req.result as PendingPhoto[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return recs.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function deletePending(localId: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite');
    t.objectStore(STORE).delete(localId);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  db.close();
}
